import { isAxiosError } from 'axios'
import { logger } from '@/lib/logger'

const DEV_HINT =
  'Start brushfire-notification-service on :4005 (`pnpm dev` in brushfire-notification-service), then refresh.'

let devNotificationBackendUnavailable = false

/** Vitest only — resets module guard between cases. */
export function resetNotificationDevBackendForTests(): void {
  devNotificationBackendUnavailable = false
}

export function isDevNotificationBackendDisabled(): boolean {
  return import.meta.env.DEV && devNotificationBackendUnavailable
}

/** True when Vite proxy cannot reach notification :4005 (ECONNREFUSED). */
export function isNotificationUnavailableError(err: unknown): boolean {
  if (!isAxiosError(err)) return false
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') return true
  const status = err.response?.status
  if (status === 502 || status === 503 || status === 504) return true
  if (import.meta.env.DEV && status === 500) return true
  return false
}

/**
 * Socket.io `connect_error` messages that mean notification :4005 is unreachable.
 * Auth failures ("Unauthorized") must NOT match — those should retry after login cookies settle.
 */
function socketConnectErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  return ''
}

export function isSocketUnreachableConnectError(err: unknown): boolean {
  const message = socketConnectErrorMessage(err).toLowerCase()
  if (!message) return false
  if (message.includes('unauthorized') || message.includes('missing subdomain')) return false
  return (
    message.includes('xhr poll error') ||
    message.includes('websocket error') ||
    message.includes('econnrefused') ||
    message.includes('err_connection_refused') ||
    message.includes('err_network') ||
    message.includes('network error') ||
    message.includes('gateway timeout') ||
    message.includes('bad gateway')
  )
}

export function markDevNotificationBackendUnavailable(cause?: unknown): void {
  if (!import.meta.env.DEV || devNotificationBackendUnavailable) return
  devNotificationBackendUnavailable = true
  const reason =
    cause instanceof Error || isAxiosError(cause) ? cause.message : undefined
  logger.warn('notification.dev_backend_unavailable', {
    reason,
    hint: DEV_HINT,
  })
}

export const notificationDevQueryOptions = {
  enabled: () => !isDevNotificationBackendDisabled(),
  retry: import.meta.env.DEV ? (false as const) : undefined,
  refetchOnWindowFocus: import.meta.env.DEV ? (false as const) : undefined,
}
